    <template lang="pug">
        div.chat-container
            template(v-if="!validConversationGroup")
                .recipients-input
                    .field-container
                        transition(name="slide" mode="out-in")
                            .field-wrapper(
                                v-if="showOrganizationField"
                                key="organization"
                            )
                                v-autocomplete(
                                    :autofocus="!validConversationGroup && showOrganizationField"
                                    clearable
                                    dense
                                    flat
                                    hide-details
                                    :items="organizations"
                                    label="Select Organization"
                                    item-text="organization_name"
                                    item-value="organization_id"
                                    :menu-props="{ maxWidth: 400, maxHeight: 150 }"
                                    open-on-clear
                                    placeholder="Select Organization"
                                    solo
                                    v-model="selectedOrganization"
                                )
                            
                            .field-wrapper(
                                v-else
                                key="recipients"
                            )
                                .d-flex.align-center
                                    v-tooltip(bottom)
                                        template(v-slot:activator='{ on, attrs }')
                                            span(v-bind="attrs" v-on="on" role="text")
                                                v-icon.ml-1(@click="backToOrganizationSelection") mdi-home-switch
                                        span
                                            | Switch Organization
                                    v-autocomplete.flex-grow-1(
                                        append-icon="mdi-plus-circle-outline"
                                        :autofocus="!validConversationGroup && !showOrganizationField"
                                        clearable
                                        chips
                                        deletable-chips
                                        dense
                                        flat
                                        hide-details
                                        :items="users"
                                        item-text="user_full_name_email"
                                        item-value="user_id"
                                        label="Add recipients"
                                        :loading="isLoadingUsers"
                                        multiple
                                        :menu-props="{ maxWidth: 400, maxHeight: 150 }"
                                        open-on-clear
                                        placeholder="Add recipients"
                                        small-chips
                                        solo
                                        v-model="recipients"
                                    )
                div(v-if="messageSending").d-flex.justify-center.fill-height.align-start.mt-2
                    span.loading-text Sending message...

            div.conversations(
                @scroll="onScrollConversationMessages"
                ref="conversationContainer"
                v-else-if="validConversationGroup"
            )
                ConversationMessages(
                    :conversation-group-id="conversationGroupId"
                    :is-group-chat="isGroupChat"
                    :message-sending="messageSending"
                    :new-message-body="bodyCopy"
                    :new-message-medias="assetsCopy"
                    ref="conversationComponent"
                    :scrollable-container="scrollableContainer"
                )

            div.footer-container
                AttachmentSlideGroup(
                    v-if="validAssets || validTmpAssets"
                    :assets="assets"
                    :tmp-assets="tmpAssets"
                    :handle-remove="removeAsset"
                )

                div.ma-0.attachments-hint(v-if="displayAttachmentsHint")
                    span.text-small(v-if="hasMaxAttachments") No more files can be added to this message (max 10).
                    span.text-small(v-if="hasTextAreaErrors" color="var(--error)") {{ textAreaErrorMessages[0] }}.

                div.text-center.ma-0(v-if="messageSending" style="width: 100%;")
                    v-progress-linear(
                        color='var(--volie-teal)'
                        indeterminate
                        :height="2"
                    )
                div.actions
                    div.buttons.mr-2
                        v-file-input.icon-btn.file-input(
                            :key="fileUploaderKey"
                            :disabled="hasMaxAttachments"
                            hide-input
                            multiple
                            accept="image/gif, image/jpeg, image/jpg, image/png"
                            @change="onMediaFileUpload"
                            :error-messages="textAreaErrorMessages"
                            prepend-icon="mdi-camera"
                        )

                        v-btn.icon-btn(
                            @click="handleDisplayEmojiPicker"
                            elevation="0"
                            rounded
                            small
                        )
                            v-icon(color="var(--volie-gray)") mdi-emoticon-outline
                    
                    v-textarea(
                        auto-grow
                        :autofocus="validConversationGroup"
                        clearable
                        :disabled="!validConversationGroup && messageSending"
                        dense
                        hide-details
                        name="message-body"
                        outlined
                        placeholder="New message"
                        ref="newMessageTextarea"
                        rows="1"
                        v-model="newMessageBody"
                        @click="updateCursorPosition"
                        @keyup="updateCursorPosition"
                        @keydown="handleMessageBodyInputKeydown"
                    )

                    v-tooltip(top)
                        template(v-slot:activator="{ on, attrs }")
                            v-btn.icon-btn.ml-2(
                                @click="handleSendMessage"
                                :disabled="disableSendButton"
                                elevation="0"
                                rounded
                                small
                                v-bind="attrs"
                                v-on="on"
                            )
                                v-icon(color="var(--volie-blue)") mdi-arrow-up-circle
                        span Send message
                div.emoji-picker(v-if="displayEmojiPicker")
                    EmojiPicker(
                        :select-emoji="selectEmoji"
                    )

    </template>

    <script lang="ts">
    import Vue from 'vue';
    import VolieState from '@/store';

    // Components
    import Attachment from '@/components/shared/attachments/attachment_card.vue';
    import AttachmentSlideGroup from '@/components/shared/attachments/attachment_slide_group.vue';
    import ConversationMessages from './conversation_messages.vue';
    import EmojiPicker from '@/components/communication_center/sms_message/emoji_picker.vue';


    //Data
    import { 
        CreateAssets,
        CreateMessage, 
        GetOrganizationOrganizationUsers,
        GetOrganizationUsers,
        MatchOrCreateUserToUserNewConversationGroup 
    } from '@/data';

    export default Vue.extend({
        data() {
            return {
                assets: [],
                assetsCopy: [],
                attachments: [],
                tmpAssets: [],
                bodyCopy: '',
                conversationGroup: {},
                cachedConversationGroupId: 0,
                displayEmojiPicker: false,
                fileUploaderKey: null,
                isLoadingUsers: false,
                messageSending: false,
                newMessageBody: '',
                newMessageTextareaCursorStartPosition: 0,
                newMessageTextareaCursorEndPosition: 0,
                organizations: [],
                recipients: [],
                scrollableContainer: null,
                selectedOrganization: null,
                textarea: null,
                textAreaErrorMessages: [],
                users: [],
            }
        },
        methods: {
            createAsset() {
                if (!(this.attachments?.length > 0)) {
                    return;
                }

                // Init formData
                const formData = new FormData();

                // Set common fields
                formData.set("account_id", (this.currentUser.activated_account_id || 0));
                formData.set("asset_id", (this.currentUser.user_id || 0));
                formData.set("asset_type", "user");

                this.attachments.forEach((attachment, idx) => {
                    const fileNum = idx + 1
                    formData.append("file-" + fileNum.toString(), attachment);
                })

                // Upload assets
                CreateAssets(formData).then((response) => {
                    if (response.Data?.assets?.length > 0) {
                        // Loop through assets and add urls to sms attachments
                        for(let i = 0; i < response.Data.assets.length; i++) {

                            // Init
                            const asset = response.Data.assets[i];
                            if (!(asset?.asset_url?.length > 0)) {
                                continue;
                            }

                            // Check if we already have this asset
                            if (this.assets?.length > 0) {
                                const assetIdx = this.assets.findIndex(x => x.id === asset.id);
                                if (assetIdx >= 0) {
                                    continue;
                                }
                            }
                            // Set asset
                            this.assets.push(asset);
                        }
                    }

                    // Empty new attachments
                    this.attachments = [];
                    this.tmpAssets = [];
                }).catch((error) => {
                    console.error('error caught when creating asset:', error);
                });
            },
            createMessage(conversationGroupId: number) {
                if (!(conversationGroupId > 0)) {
                    return;
                }

                const options = {
                    account_id: this.currentUser.activated_account_id,
                    assets: this.assetsCopy || [],
                    body: this.bodyCopy,
                    conversation_group_id: conversationGroupId,
                }
                CreateMessage(options).then((response) => {
                    if (response?.Data) {
                        this.$emit('view-conversation', conversationGroupId);
                        this.recipients = [];
                    }
                    if (response?.Error) {
                        console.error('error creating message:', response?.Error);
                    }
                })
                .catch((error) => {
                    console.error('error caught when creating message:', error);
                })
                .finally(() => {
                    this.messageSending = false;
                    this.bodyCopy = '';
                    this.assetsCopy = [];
                });
            },
            getOrganizationUsers() {
                GetOrganizationUsers().then((response) => {
                    if (response?.Data?.organization_users.length > 0) {
                        this.organizations = response?.Data?.organization_users;
                    }
                })
                .catch((error) => {
                    console.error('error caught when getting organizations:', error);
                });
            },
            getOrganizationOrganizationUsers() {
                if (!this.selectedOrganization) {
                    return;
                }
                this.isLoadingUsers = true;

                GetOrganizationOrganizationUsers(this.selectedOrganization).then((response) => {
                    if (response?.Data?.organization_organization_users?.length > 0) {
                        this.users = response.Data.organization_organization_users.filter((user: any) => user.user_id !== this.currentUser.user_id);

                        if (!this.currentUserIsSuperAdmin) {
                            this.users = this.users.filter((user: any) => user.role_id !== 1);
                        }
                    }
                })
                .catch((error) => {
                    console.error('error caught when getting organization organization users:', error);
                })
                .finally(() => {
                    this.isLoadingUsers = false;
                });
            },
            handleDisplayEmojiPicker() {
                this.displayEmojiPicker = !this.displayEmojiPicker;
            },
            handleMessageBodyInputKeydown(e: KeyboardEvent) {
                if (e.key?.toLowerCase() === "enter" && !e.shiftKey) {
                    this.handleSendMessage();
                    e.preventDefault();
                }
            },
            async handleSendMessage() {
                if (!this.eligibleToSend) {
                    return;
                }
                
                this.bodyCopy = this.newMessageBody;
                this.assetsCopy = this.assets;
                this.resetMessageState();
                this.messageSending = true;
                
                if (this.validConversationGroup) {
                    return this.createMessage(this.conversationGroupId);
                } else {
                    //for new conversation groups
                    const matchedOrCreatedConversationGroupId = await this.matchOrCreateUserToUserNewConversationGroup()
                    if (matchedOrCreatedConversationGroupId > 0) {
                        return this.createMessage(matchedOrCreatedConversationGroupId);
                    }

                    console.error('no conversation group ID found');
                    this.messageSending = false;
                    return;
                }
                    
            },
            init() {
                if (!this.validConversationGroup) {
                    this.getOrganizationUsers();
                }
            },
            async matchOrCreateUserToUserNewConversationGroup() {
                const options = {
                    user_id: this.currentUser.user_id,
                    other_user_ids: this.recipients,
                }
                try {
                    const response = await MatchOrCreateUserToUserNewConversationGroup(options);
                    if (response?.Data) {
                        return response?.Data?.conversation_group?.id;
                    }
                    console.error('matchOrCreateUserToUserNewConversationGroup error', response?.Error);
                } catch (error) {
                    console.error('matchOrCreateUserToUserNewConversationGroup error', error);
                }
            },
            onMediaFileUpload(files: any) {
                this.textAreaErrorMessages = [];

                if (!(files?.length > 0)) {
                    return;
                }

                for (let i = 0; i < files.length; i++) {
                    const file = files[i];

                    if (file.size >= 5000000) {
                        this.textAreaErrorMessages.push('One or more files was not added (exceeds max 5 MB)');
                        continue;
                    }

                    const totalAssetsLength = this.assets?.length + this.attachments?.length;
                    if (this.assets?.length < 10 && file.size <= 5000000 && totalAssetsLength < 10) {
                        this.attachments.push(file);
                        this.tmpAssets.push({ isTemp: true });
                    }
                }

                this.createAsset();

                // Permits uploading same file again (in case they add/remove/add)
                this.fileUploaderKey = Date.now();
            },
            onScrollConversationMessages() {
                if (this.$refs.conversationComponent) {
                    this.$refs.conversationComponent.onScrollConversationMessages();
                }
            },
            removeAsset(asset: any) {
                if (!asset) {
                    return;
                }

                const idxToRemove = this.assets.findIndex(x => x.id === asset.id);
                if (idxToRemove >= 0) {
                    this.assets.splice(idxToRemove, 1);
                }
            },
            resetMessageState() {
                this.assets = [];
                this.tmpAssets = [];
                this.newMessageBody = '';
                this.textAreaErrorMessages = [];
            },
            selectEmoji(emoji: any) {
                if (!this.textarea) {
                    return;
                }
                // Insert emoji
                this.newMessageBody = this.newMessageBody.substring(0, this.newMessageTextareaCursorStartPosition) + emoji + this.newMessageBody.substring(this.newMessageTextareaCursorEndPosition);
                
                // Move cursor position value to after emoji
                this.newMessageTextareaCursorStartPosition += emoji?.length || 0;

                // If text was highlighted to be replaced by emoji, reset highlighting since replaced with emoji with first insert
                this.newMessageTextareaCursorEndPosition = this.newMessageTextareaCursorStartPosition;

                // actually update cursor position and focus textarea
                this.$nextTick(() => {
                    this.textarea.selectionStart = this.newMessageTextareaCursorStartPosition;
                    this.textarea.selectionEnd = this.newMessageTextareaCursorEndPosition;
                    this.textarea.focus();
                });
            },
            updateCursorPosition() {
                if (this.textarea) {
                    this.newMessageTextareaCursorStartPosition = this.textarea.selectionStart;
                    this.newMessageTextareaCursorEndPosition = this.textarea.selectionEnd;
                }
            },
            backToOrganizationSelection() {
                this.selectedOrganization = null;
                this.recipients = [];
                this.users = [];
            },
        },
        computed: {
            currentUser() {
                return VolieState.currentUser();
            },
            currentUserIsSuperAdmin() {
                return this.currentUser.activated_role_id === 1;
            },
            eligibleToSend() {
                if (!this.validConversationGroup) {
                    return !this.emptyRecipients && (this.validAssets || !this.emptyMessageBody);
                }
                return this.validAssets || !this.emptyMessageBody;
            },
            displayAttachmentsHint() {
                return this.hasMaxAttachments || this.textAreaErrorMessages.length > 0;
            },
            disableSendButton() {
                return !this.eligibleToSend || (this.messageSending && !this.validConversationGroup);
            },
            emptyMessageBody() {
                return this.newMessageBody.trim() === '';
            },
            emptyRecipients() {
                return this.recipients.length === 0;
            },
            hasMaxAttachments() {
                return this.assets?.length >= 10;
            },
            hasTextAreaErrors() {
                return this.textAreaErrorMessages.length > 0;
            },
            validConversationGroup() {
                return this.conversationGroupId > 0;
            },
            validAssets() {
                return this.assets?.length > 0;
            },
            validTmpAssets() {
                return this.tmpAssets?.length > 0;
            },
            showOrganizationField() {
                return !this.selectedOrganization;
            },
        },
        watch: {
            validConversationGroup(newVal) {
                if (newVal) {
                    this.$nextTick(() => {
                        this.scrollableContainer = this.$refs.conversationContainer || null;
                    });
                }
            },
            conversationGroupId(newVal) {
                this.textAreaErrorMessages = [];

                // if this cg id is the same as the cached id, don't do anything
                if (newVal === this.cachedConversationGroupId || newVal === 0) {
                    return;
                }

                // if different, set new cached conversation group id and reset message state
                this.cachedConversationGroupId = newVal;
                this.resetMessageState();
            },
            selectedOrganization(newVal) {
                this.recipients = [];
                this.users = [];
                if (newVal) {
                    this.getOrganizationOrganizationUsers();
                }
            },
        },
        components: {
            Attachment,
            AttachmentSlideGroup,
            ConversationMessages,
            EmojiPicker,
        },
        props: {
            conversationGroupId: Number,
            isGroupChat:Boolean,
        },
        created() {
            this.init();
        },
        mounted() {
            // Get actual html textarea element from within v-textarea
            this.textarea = this.$refs.newMessageTextarea.$el.querySelector('textarea');
        },
    })
    </script>

    <style lang="scss" scoped>
        .chat-container {
            height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: space-between;

            .conversations {
                display: flex;
                height: 100%;
                overflow-y: auto;
            }

            .recipients-input {
                border-bottom: 1px solid var(--lt-gray);
                padding: 5px;

                .field-container {
                    position: relative;
                    overflow: hidden;
                }

                .field-wrapper {
                    width: 100%;
                }

                .slide-enter-active, .slide-leave-active {
                    transition: all 0.2s ease-in-out;
                }

                .slide-enter {
                    transform: translateX(100%);
                    opacity: 0;
                }

                .slide-leave {
                    transform: translateX(-100%);
                    opacity: 0;
                }
                
            }

            .recipients-hint {
                color: var(--volie-gray);
                font-size: 12px;
                margin-left: 12px;
            }

            .footer-container {
                min-height: fit-content;
                display: flex;
                flex-direction: column;
                justify-content: space-between;
                align-items: center;
                background-color: var(--off-white);

                ::v-deep div.v-textarea {
                    .v-input__control {
                        position: relative !important;
                        max-height: 150px !important;
                        background-color: white !important;
                    }

                    .v-input__slot {
                        max-height: 150px !important;
                        padding: 5px !important;
                        justify-content: space-between !important;
                    }

                    textarea {
                        margin: 0 !important;
                        padding: 5px !important;
                        font-size: 14px;
                        line-height: 1.5;
                        width: 100%;
                        border-radius: 5px;
                        overflow-y: auto;
                        max-height: 125px !important;
                    }
                }
    
                .actions {
                    display: flex;
                    padding: 10px;
                    flex-direction: row;
                    align-items: center;
                    width: 100%;
                    
                    .buttons {
                        display: flex;
                        flex-direction: row;
                        align-items: center;

                        .file-input {
                            border-radius: 50%;
                            transition: background-color 0.3s ease;

                            &:hover {
                                background-color: var(--lt-gray);
                            }
                        }

                        ::v-deep .v-input__prepend-outer {
                            margin: 0;
                        }
                    }

                    .icon-btn {
                        min-width: 0 !important;
                        min-height: 0 !important;
                        width: fit-content;
                        height: fit-content;
                        padding: 5px;
                        background-color: var(--off-white);
                    }
                }

                .attachments-hint {
                    width: 100%;
                    color: var(--volie-gray);
                    text-align: center;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                }

                .emoji-picker {
                    width: 100%;
                }
            }
        }
    </style>